/**
 * Ad size profiles define how each creative size should be rendered
 * in device mocks — placement position, best device type, and capture settings.
 */

import { DeviceType } from '../types/previewStudio';

export type AdPlacement = 'mid-content' | 'top-banner' | 'bottom-banner' | 'interstitial' | 'sidebar';

export interface SizeProfile {
  placement: AdPlacement;
  bestDevice: DeviceType;
  /** Whether to show site context content around the ad */
  showSiteContext: boolean;
  /** For interstitials, overlay the ad centered on a dimmed background */
  isOverlay: boolean;
  /** Lambda capture viewport — should match outer device frame dimensions */
  captureWidth: number;
  captureHeight: number;
  /** Description for UI display */
  label: string;
}

const MOBILE_FRAME = { captureWidth: 415, captureHeight: 707 };
const TABLET_FRAME = { captureWidth: 876, captureHeight: 1236 };
const DESKTOP_FRAME = { captureWidth: 1464, captureHeight: 988 };

const SIZE_PROFILES: Record<string, SizeProfile> = {
  // Mobile banners
  '320x50': {
    placement: 'bottom-banner',
    bestDevice: 'mobile',
    showSiteContext: true,
    isOverlay: false,
    ...MOBILE_FRAME,
    label: 'Mobile Banner',
  },
  '300x50': {
    placement: 'bottom-banner',
    bestDevice: 'mobile',
    showSiteContext: true,
    isOverlay: false,
    ...MOBILE_FRAME,
    label: 'Mobile Banner',
  },

  // Mobile rectangles
  '300x250': {
    placement: 'mid-content',
    bestDevice: 'mobile',
    showSiteContext: true,
    isOverlay: false,
    ...MOBILE_FRAME,
    label: 'Medium Rectangle',
  },
  '336x280': {
    placement: 'mid-content',
    bestDevice: 'mobile',
    showSiteContext: true,
    isOverlay: false,
    ...MOBILE_FRAME,
    label: 'Large Rectangle',
  },

  // Mobile interstitials
  '320x480': {
    placement: 'interstitial',
    bestDevice: 'mobile',
    showSiteContext: false,
    isOverlay: true,
    ...MOBILE_FRAME,
    label: 'Mobile Interstitial',
  },
  '300x600': {
    placement: 'interstitial',
    bestDevice: 'mobile',
    showSiteContext: false,
    isOverlay: true,
    ...MOBILE_FRAME,
    label: 'Half-Page / Interstitial',
  },
  '375x667': {
    placement: 'interstitial',
    bestDevice: 'mobile',
    showSiteContext: false,
    isOverlay: true,
    ...MOBILE_FRAME,
    label: 'Full-Screen Mobile',
  },

  // Tablet / Desktop leaderboards
  '728x90': {
    placement: 'top-banner',
    bestDevice: 'tablet',
    showSiteContext: true,
    isOverlay: false,
    ...TABLET_FRAME,
    label: 'Leaderboard',
  },
  '970x90': {
    placement: 'top-banner',
    bestDevice: 'desktop',
    showSiteContext: true,
    isOverlay: false,
    ...DESKTOP_FRAME,
    label: 'Super Leaderboard',
  },
  '970x250': {
    placement: 'top-banner',
    bestDevice: 'desktop',
    showSiteContext: true,
    isOverlay: false,
    ...DESKTOP_FRAME,
    label: 'Billboard',
  },

  // Desktop sidebar
  '160x600': {
    placement: 'sidebar',
    bestDevice: 'desktop',
    showSiteContext: true,
    isOverlay: false,
    ...DESKTOP_FRAME,
    label: 'Wide Skyscraper',
  },
  '120x600': {
    placement: 'sidebar',
    bestDevice: 'desktop',
    showSiteContext: true,
    isOverlay: false,
    ...DESKTOP_FRAME,
    label: 'Skyscraper',
  },

  // Video (16:9 standard resolutions)
  '320x180': {
    placement: 'mid-content',
    bestDevice: 'mobile',
    showSiteContext: false,
    isOverlay: false,
    ...MOBILE_FRAME,
    label: 'Video (320x180)',
  },
  '480x270': {
    placement: 'mid-content',
    bestDevice: 'tablet',
    showSiteContext: false,
    isOverlay: false,
    ...TABLET_FRAME,
    label: 'Video (480x270)',
  },
  '768x432': {
    placement: 'mid-content',
    bestDevice: 'tablet',
    showSiteContext: false,
    isOverlay: false,
    ...TABLET_FRAME,
    label: 'Video (768x432)',
  },
  '1024x576': {
    placement: 'mid-content',
    bestDevice: 'desktop',
    showSiteContext: false,
    isOverlay: false,
    ...DESKTOP_FRAME,
    label: 'Video (1024x576)',
  },

  // Tablet
  '768x1024': {
    placement: 'interstitial',
    bestDevice: 'tablet',
    showSiteContext: false,
    isOverlay: true,
    ...TABLET_FRAME,
    label: 'Tablet Interstitial',
  },
};

/**
 * Get the size profile for a given ad size string (e.g., "300x250").
 * Falls back to a sensible default based on aspect ratio.
 */
export function getSizeProfile(size: string): SizeProfile {
  // Exact match
  if (SIZE_PROFILES[size]) {
    return SIZE_PROFILES[size];
  }

  // Infer from dimensions
  const [w, h] = size.split('x').map(Number);
  if (!w || !h) {
    return SIZE_PROFILES['300x250']; // safe default
  }

  const area = w * h;
  const ratio = w / h;

  // Very wide (banners)
  if (ratio > 3) {
    return {
      placement: w > 400 ? 'top-banner' : 'bottom-banner',
      bestDevice: w > 400 ? 'tablet' : 'mobile',
      showSiteContext: true,
      isOverlay: false,
      ...(w > 400 ? TABLET_FRAME : MOBILE_FRAME),
      label: 'Banner',
    };
  }

  // Very tall (skyscrapers)
  if (ratio < 0.35) {
    return {
      placement: 'sidebar',
      bestDevice: 'desktop',
      showSiteContext: true,
      isOverlay: false,
      ...DESKTOP_FRAME,
      label: 'Skyscraper',
    };
  }

  // Large area = interstitial
  if (area > 100000 || (h > 400 && ratio < 1.2)) {
    return {
      placement: 'interstitial',
      bestDevice: w > 500 ? 'tablet' : 'mobile',
      showSiteContext: false,
      isOverlay: true,
      ...(w > 500 ? TABLET_FRAME : MOBILE_FRAME),
      label: 'Interstitial',
    };
  }

  // Default: mid-content rectangle
  return {
    placement: 'mid-content',
    bestDevice: w > 400 ? 'tablet' : 'mobile',
    showSiteContext: true,
    isOverlay: false,
    ...(w > 400 ? TABLET_FRAME : MOBILE_FRAME),
    label: 'Rectangle',
  };
}
